import React from 'react';
import { motion } from 'framer-motion';

export default function Terms() {
  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc]" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm border border-slate-200"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 font-['Oswald']">
            Terms & Conditions
          </h1>
          <p className="text-slate-500 text-sm mb-8 pb-6 border-b border-slate-100">
            Last Updated: July 21, 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
            <p>
              Welcome to VHFMS (Venkateswara Hospitality & Facilities Management Services). By accessing or using the VHFMS mobile application or website, you agree to comply with and be bound by these Terms & Conditions. If you do not agree with any part of these terms, please do not use our services.
            </p>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">1. Acceptance of Terms</h2>
              <p>
                By creating an account or using VHFMS, you confirm that you have read, understood, and agreed to these Terms & Conditions and our Privacy Policy.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">2. Services</h2>
              <p>
                VHFMS provides a platform that enables users to browse, book, and manage hospitality and facility management services. The availability of services may vary depending on your location and service provider availability.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">3. User Account</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You agree to provide accurate and up-to-date information during registration.</li>
                <li>You are responsible for all activities performed through your account.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">4. Booking & Scheduling</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Bookings are subject to service provider availability.</li>
                <li>Users must provide accurate booking details, including address, contact information, and preferred schedule.</li>
                <li>VHFMS reserves the right to reschedule or cancel bookings due to unforeseen circumstances.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">5. Payments</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Payments must be made using the payment methods available within the application.</li>
                <li>All applicable taxes and charges will be displayed before confirming the booking.</li>
                <li>Refunds, if applicable, will be processed according to our Refund Policy.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">6. Cancellation Policy</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Users may cancel bookings according to the cancellation policy displayed during booking.</li>
                <li>Cancellation charges may apply depending on the timing of the cancellation.</li>
                <li>VHFMS reserves the right to cancel bookings in exceptional circumstances.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">7. User Responsibilities</h2>
              <p className="mb-2">Users agree not to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide false or misleading information.</li>
                <li>Misuse, interfere with, or disrupt the platform.</li>
                <li>Use the services for any unlawful or fraudulent purpose.</li>
                <li>Attempt to gain unauthorized access to the platform or user accounts.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">8. Ratings & Reviews</h2>
              <p>
                Users may provide ratings and reviews based on their genuine service experience. VHFMS reserves the right to remove inappropriate, offensive, misleading, or fraudulent content.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">9. Intellectual Property</h2>
              <p>
                All content, logos, trademarks, designs, graphics, and software associated with VHFMS are the property of VHFMS or its licensors. Unauthorized copying, reproduction, or distribution is strictly prohibited.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">10. Privacy</h2>
              <p>
                Your use of VHFMS is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">11. Limitation of Liability</h2>
              <p>
                VHFMS shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of the application or services, except where required by applicable law.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">12. Service Availability</h2>
              <p>
                While we strive to provide uninterrupted services, VHFMS does not guarantee that the application or services will always be available without interruptions, delays, or technical issues.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">13. Suspension or Termination</h2>
              <p>
                We reserve the right to suspend or terminate any account that violates these Terms & Conditions or engages in fraudulent, abusive, or illegal activities.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">14. Changes to Terms</h2>
              <p>
                VHFMS may update these Terms & Conditions from time to time. Updated versions will be published within the application and on the website. Continued use of the services after changes are posted constitutes acceptance of the revised terms.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">15. Governing Law</h2>
              <p>
                These Terms & Conditions shall be governed by and interpreted in accordance with the laws of India. Any disputes arising from the use of VHFMS shall be subject to the jurisdiction of the competent courts in India.
              </p>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">16. Contact Us</h2>
              <p className="mb-2">If you have any questions regarding these Terms & Conditions, please contact us:</p>
              <p className="font-semibold text-slate-900">VHFMS (Venkateswara Hospitality & Facilities Management Services)</p>
              <p>Email: <a href="mailto:vhfms.info@gmail.com" className="text-blue-600 hover:underline">vhfms.info@gmail.com</a></p>
              <p>Website: <a href="http://www.vhfms.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.vhfms.com</a></p>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
